import { Box, IconButton, SvgIcon } from '@mui/material';
import type { Node } from '@tiptap/pm/model';
import { type Editor, NodeViewWrapper } from '@tiptap/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, palette, spaces } from '../../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../getMuiIcon';
import useScreenSize from '../../../useScreenSize';

interface ImageBlockViewProps {
	editor: Editor;
	getPos: () => number;
	node: Node & {
		attrs: {
			src: string;
			alt?: string;
			width?: string;
		};
	};
	selected: boolean;
	updateAttributes: (attrs: Record<string, string>) => void;
}

export const ImageBlockView = (props: ImageBlockViewProps) => {
	const { editor, getPos, node, selected, updateAttributes } = props;
	const { isXsScreen } = useScreenSize();
	const { t } = useTranslation();
	const imageWrapperRef = useRef<HTMLDivElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);
	const { src, alt, width } = node.attrs;
	const altText = alt || t('Editor image');
	const [isResizing, setIsResizing] = useState(false);
	const [currentWidth, setCurrentWidth] = useState<string>(width || '100%');

	const onClick = useCallback(() => {
		editor.commands.setNodeSelection(getPos());
	}, [getPos, editor.commands]);

	// Update current width when node attributes change, but only when NOT resizing
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (!isResizing && width && width !== currentWidth) {
			setCurrentWidth(width);
		}
	}, [width, isResizing]);

	const handleResizeStart = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();

			if (!imageRef.current || !imageWrapperRef.current) return;

			setIsResizing(true);
			const startX = e.clientX;
			const startWidth = imageRef.current.getBoundingClientRect().width;
			const containerWidth = imageWrapperRef.current.getBoundingClientRect().width;
			const minWidth = 100; // Minimum 100px
			const maxWidth = containerWidth;

			const handleMouseMove = (moveEvent: MouseEvent) => {
				moveEvent.preventDefault();
				const deltaX = moveEvent.clientX - startX;
				const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));
				const widthPercent = Math.round((newWidth / containerWidth) * 100);

				setCurrentWidth(`${widthPercent}%`);
			};

			const handleMouseUp = (upEvent: MouseEvent) => {
				upEvent.preventDefault();
				setIsResizing(false);

				// Save the final width
				if (imageWrapperRef.current) {
					const finalWidth = imageRef.current?.getBoundingClientRect().width || startWidth;
					const containerWidth = imageWrapperRef.current.getBoundingClientRect().width;
					const widthPercent = Math.max(
						10,
						Math.min(100, Math.round((finalWidth / containerWidth) * 100))
					);
					const finalWidthStr = `${widthPercent}%`;

					setCurrentWidth(finalWidthStr);
					updateAttributes({ width: finalWidthStr });
				}

				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};

			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);
		},
		[updateAttributes]
	);

	return (
		<NodeViewWrapper>
			<Box
				contentEditable={false}
				ref={imageWrapperRef}
				sx={{
					display: 'flex',
					justifyContent: 'center',
					width: '100%',
					position: 'relative',
					userSelect: isResizing ? 'none' : 'auto',
					paddingY: spaces.sm,
				}}
			>
				<Box
					sx={{
						position: 'relative',
						display: 'inline-block',
						maxWidth: '100%',
						width: isXsScreen ? '100%' : currentWidth,
					}}
				>
					<Box
						data-testid="image-block-view-image"
						ref={imageRef}
						component="img"
						src={src}
						alt={altText}
						className="block"
						onClick={onClick}
						draggable={false}
						onDragStart={(e: React.DragEvent) => e.preventDefault()}
						sx={{
							border: selected ? `2px solid ${palette.primary.main}` : 'none',
							borderRadius: borderRadius.xs,
							maxWidth: '100%',
							width: '100%',
							height: 'auto',
							objectFit: 'contain',
							cursor: isResizing ? 'ew-resize' : 'pointer',
							display: 'block',
							transition: isResizing ? 'none' : 'border 0.2s ease',
							margin: '0 !important',
						}}
					/>
					{selected && !isXsScreen && (
						<>
							<IconButton
								data-drag-handle
								color="outline"
								sx={{
									position: 'absolute',
									top: '-8px',
									left: '-16px',
								}}
							>
								<SvgIcon sx={{ width: 24, height: 24 }} component={getMuiIcon('move')} />
							</IconButton>
							<Box
								onMouseDown={handleResizeStart}
								sx={{
									position: 'absolute',
									right: '-4px',
									bottom: '-4px',
									width: '20px',
									height: '20px',
									background: palette.primary.main,
									cursor: 'nwse-resize',
									borderRadius: '50%',
									border: '2px solid white',
									boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
									zIndex: 10,
									'&:hover': {
										background: palette.primary.dark,
										transform: 'scale(1.1)',
									},
									transition: 'all 0.2s ease',
								}}
							/>
							<Box
								sx={{
									position: 'absolute',
									top: '-24px',
									right: '0',
									background: palette.primary.main,
									color: 'white',
									padding: '2px 8px',
									borderRadius: borderRadius.xs,
									fontSize: '12px',
									fontWeight: 500,
									pointerEvents: 'none',
									opacity: isResizing ? 1 : 0,
									transition: 'opacity 0.2s ease',
								}}
							>
								{currentWidth}
							</Box>
						</>
					)}
				</Box>
			</Box>
		</NodeViewWrapper>
	);
};

export default ImageBlockView;
